/**
 * Логика нумерации маркеров.
 *
 * Правила (из ТЗ):
 *  - Новый маркер получает max(существующих) + 1 (или 1, если номеров нет).
 *  - При удалении маркера остальные пересчитываются по позиции в упорядоченном
 *    списке, чтобы последовательность оставалась непрерывной (1, 2, 3 ...).
 *  - «Renumber all» — пересчёт всех номеров по текущему z-порядку.
 */
import type { Shape } from './types';

/** Извлечь числовое значение маркера из shape (если есть). */
export function getNumberValue(shape: Shape): number | null {
  if (shape.type === 'number') return shape.value;
  if (shape.type === 'arrow' || shape.type === 'rect') {
    if (shape.marker && shape.marker.kind === 'number') return shape.marker.value;
  }
  return null;
}

/** Следующий порядковый номер для нового маркера. */
export function nextNumberValue(shapes: Shape[]): number {
  let max = 0;
  for (const s of shapes) {
    const v = getNumberValue(s);
    if (v !== null && v > max) max = v;
  }
  return max + 1;
}

/** Все shape, несущие номер, упорядоченные по z (меньше z = раньше в последовательности). */
export function numberedShapesSorted(shapes: Shape[]): Shape[] {
  return shapes
    .filter((s) => getNumberValue(s) !== null)
    .sort((a, b) => a.z - b.z || a.createdAt - b.createdAt);
}

/**
 * Перенумеровать все маркеры по z-порядку: 1, 2, 3 ...
 * Возвращает новый массив shapes (иммутабельно) или исходный, если ничего не поменялось.
 */
export function renumberAll(shapes: Shape[]): Shape[] {
  const sorted = numberedShapesSorted(shapes);
  if (sorted.length === 0) return shapes;
  const byId = new Map<string, Shape>();
  for (const s of shapes) byId.set(s.id, s);

  let changed = false;
  sorted.forEach((s, idx) => {
    const want = idx + 1;
    const cur = byId.get(s.id)!;
    const updated = withNumber(cur, want);
    if (updated !== cur) {
      byId.set(s.id, updated);
      changed = true;
    }
  });

  if (!changed) return shapes;
  return shapes.map((s) => byId.get(s.id) ?? s);
}

/** Присвоить shape новое числовое значение маркера (иммутабельно). */
export function withNumber(shape: Shape, value: number): Shape {
  if (shape.type === 'number') {
    if (shape.value === value) return shape;
    return { ...shape, value };
  }
  if (shape.type === 'arrow' || shape.type === 'rect') {
    if (shape.marker && shape.marker.kind === 'number' && shape.marker.value === value) return shape;
    return { ...shape, marker: { kind: 'number', value } };
  }
  return shape;
}

/**
 * Найти коллизию: есть ли уже маркер с таким значением (кроме самого shape).
 * Возвращает id конфликтующего shape или null.
 */
export function findNumberCollision(shapes: Shape[], exceptId: string, value: number): string | null {
  for (const s of shapes) {
    if (s.id === exceptId) continue;
    if (getNumberValue(s) === value) return s.id;
  }
  return null;
}
